<template>
  <div class="health-home">
    <WidgetsRendererContainer
      v-if="healthMainWidgets"
      :widgets="healthMainWidgets"
    />
    <div class="health-home__articles-title">Ещё по теме</div>
    <div
      v-if="articles && articles.length"
      class="health-articles"
    >
      <ArticleCardSmall
        v-for="(article, index) in articles"
        :key="article.id"
        class="health-articles__article"
        v-bind="useArticleProps(article)"
        :loading-views="getLoadingViews(article.id)"
        @click-on-article-card="sendAnalyticsSmallArticleCard(article, index + 1)"
        @clicked-on-category="onClickOnCategory"
        @click-on-restore-button="updateViewsCount(article.id)"
        @favorite-delete="
          (favorite) => {
            onFavoriteDelete(favorite, article, false);
            sendAnalyticsFavoriteDeleteEvent(article);
          }
        "
        @favorite-create-folder-and-transfer="
          (nameFolder) => {
            onFavoriteCreateFolderAndAdd(nameFolder, article, false);
          }
        "
        @favorite-create-folder-and-add="
          (nameFolder) => {
            onFavoriteCreateFolderAndAdd(nameFolder, article, false);
            sendAnalyticsFavoriteAddEvent(article);
          }
        "
        @favorite-transfer-to-folder="
          (favorite, folder) => onFavoriteTransferToFolder(favorite, folder, article, update)
        "
        @favorite-add-to-folder="
          (folder) => {
            onFavoriteAddToFolder(folder, article);
            sendAnalyticsFavoriteAddEvent(article);
          }
        "
        @favorite-deferred="onFavoriteDeferred"
      />
      <a
        class="health-articles__show-more"
        href="/health/list/?page=2"
        @click="sendAnalyticsShowMore"
      >
        Показать ещё
      </a>
    </div>
  </div>
</template>

<script lang="ts">
  import ArticleCardSmall from '@/components/lh-ui/ArticleCard/ArticleCardSmall/ArticleCardSmall.vue';
  import WidgetsRendererContainer from '@/containers/WidgetsRendererContainer.vue';

  import { useArticleProps } from '~/composables/useArticleProps';
  import { trimWpDomainInPlace } from '~/utils/trimWpDomain';
  import { getFullUrl } from '~/utils';
  import { useWidgetsStore } from '~/store/widgets';
  import { useArticlesStore } from '~/store/articles';
  import { useFavorite } from '~/composables/useFavoriteMethods';
  import { useUpdatingViewsCount } from '~/composables/useUpdatingViewsCount';
  import { usePageStore } from '~/store/pagesData';
  import type { IArticle } from '~/types/typings';
  import { PageSeoInputSchema } from '~/domains/head/seo/api/transformSeo';
  import { parseSeoOrThrow } from '~/domains/head/seo/services/parseSeoOrThrow';
  import { useSeoHead } from '~/domains/head/seo/services/useSeoHead';

  export default defineNuxtComponent({
    name: 'HealthHome',

    components: {
      ArticleCardSmall,
      WidgetsRendererContainer,
    },

    async setup() {
      definePageMeta({ layout: 'health' });

      const app = useNuxtApp();
      const pageStore = usePageStore();
      const articlesStore = useArticlesStore();
      const widgetsStore = useWidgetsStore();

      const {
        onFavoriteDelete,
        onFavoriteCreateFolderAndAdd,
        onFavoriteCreateFolderAndTransfer,
        onFavoriteTransferToFolder,
        onFavoriteAddToFolder,
        onFavoriteDeferred,
      } = useFavorite();
      const { loadingViewsIds, updateViewsCount, getLoadingViews } = useUpdatingViewsCount();

      const { data } = await useAsyncData(`HEALTH_HOME`, async () => {
        try {
          const homeData = (await app.$healthApi.fetchHome()).data;
          trimWpDomainInPlace(homeData, ['items.url', 'items.categories.url']);
          const seo = parseSeoOrThrow(PageSeoInputSchema, homeData.seo);

          pageStore.addArchiveData({
            data: {
              icon: homeData.icon,
              title: homeData.title,
              totalPages: homeData.totalPages,
              articles: homeData.items,
              seo,
            },
          });

          return homeData;
        } catch (err) {
          console.error('$healthApi.fetchHome error', err);
          throw createError({
            statusCode: err?.status ?? 404,
            statusMessage: 'Page Not Found',
          });
        }
      });

      useSeoHead(computed(() => pageStore.currentSeo!));

      return {
        homeData: data.value,
        onFavoriteDelete,
        onFavoriteCreateFolderAndAdd,
        onFavoriteCreateFolderAndTransfer,
        onFavoriteTransferToFolder,
        onFavoriteAddToFolder,
        onFavoriteDeferred,
        articlesStore,
        loadingViewsIds,
        updateViewsCount,
        getLoadingViews,
        widgetsStore,
      };
    },

    computed: {
      healthMainWidgets() {
        return this.widgetsStore.currentHealthMainPage;
      },

      favorites() {
        return this.articlesStore.userRelated.favorites;
      },

      articles(): IArticle[] | undefined {
        return this.homeData?.items;
      },
    },

    mounted() {
      if (this.articles) {
        const includes = this.articles.map((item) => item.id);

        this.articlesStore.fetchAndSetFavorites({
          post_ids: includes.join(),
          per_page: includes.length,
        });
      }
    },

    methods: {
      useArticleProps,

      sendAnalyticsSmallArticleCard(article: IArticle, order: number) {
        this.$sendYandexMetrika({
          level1: 'Выбор материала_Списочная карточка',
          level4: getFullUrl(article.url),
          level5: order,
          level6: 'Ещё по теме',
          level8: article.teaserUrl ? 'Тизер' : 'Списочная карточка',
        });
      },

      sendAnalyticsShowMore() {
        this.$sendYandexMetrika({
          level1: 'Клик_Показать еще_Списочная карточка',
          level4: getFullUrl('/health/list/?page=2'),
          level6: 'Ещё по теме',
        });
      },

      onClickOnCategory(link: string, titleCategory: string, _title: string): void {
        this.$sendYandexMetrika({
          level1: 'Выбор рубрики',
          level4: getFullUrl(link),
          level5: titleCategory,
          level6: 'Ещё по теме',
        });
      },

      sendAnalyticsFavoriteDeleteEvent(article: IArticle) {
        this.$sendYandexMetrika({
          level1: 'Избранное_удаление избранного',
          level4: getFullUrl(article.url),
          level6: 'Ещё по теме',
        });
      },

      sendAnalyticsFavoriteAddEvent(article: IArticle) {
        this.$sendYandexMetrika({
          level1: 'Избранное_добавление избранного',
          level4: getFullUrl(article.url),
          level6: 'Ещё по теме',
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  .health-home {
    background-color: #fff;
    display: flex;
    flex-direction: column;

    &__articles-title {
      font-weight: 600;
      font-size: 26px;
      line-height: 34px;
      font-family: $secondFont;
      color: #000;
      padding: 16px 0 16px 16px;
      border-top: 1px solid #e7e7e7;
      // * для того, чтобы обводки друг на друга не накладывались
      margin-top: -2px;
      @include tablet {
        font-size: 28px;
        line-height: 36px;
        padding: 24px 0 16px 24px;
      }
    }

    &:deep() {
      .lh-large-article-card__wrapper {
        min-width: auto;
        border: 0;
        @include big-tablet {
          width: 830px;
        }
      }

      .taxonomy-posts__container {
        .slider {
          border-top: none;
        }
      }

      .recommendation-block {
        margin: 0;
        border: 0;

        &__title {
          // undoes the margin from the widgers renderer on home page
          margin: 0;
        }
      }

      .before-after-comments-wrapp {
        border: 0;
      }

      .widgets-renderer {
        @include big-tablet {
          max-width: 832px;
        }
        & > section:not(:first-child) {
          border-top: 1px solid #e7e7e7;
        }
      }

      .add-margin-bottom {
        margin-bottom: 0;
      }
    }
  }

  .health-articles {
    margin-top: 0;

    &__article + &__article {
      position: relative;

      &:before {
        content: '';
        left: 16px;
        right: 16px;
        top: 0;
        display: block;
        position: absolute;
        border-top: 1px solid #e7e7e7;

        @include tablet {
          left: 24px;
          right: 24px;
        }
      }
    }
    &__show-more {
      @include fontTextBtn;

      display: flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
      margin: 8px 16px 24px;
      width: calc(100% - 32px);
      height: 56px;
      background: var(--main-color);
      color: white;
      border-radius: 2px;

      @include tablet {
        width: calc(100% - 48px);
        margin: 8px 24px 32px;
      }
      @include hover {
        background: var(--hover-color);
      }
    }
  }
</style>
